import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback } from 'react';
import { specificResourceRequestWithAllocationsFragment } from '~/modules/resourcing/common/fragments';
import { HiringStatus, ResourceRequestStatus } from '~/types';
import RESOURCE_REQUESTS_ALLOCATIONS_QUERY from '~/modules/resourcing/common/fragments/resourceRequestsQuery';
import { RESOURCE_REQUESTS_ALLOCATIONS_QUERY as RESOURCING_RESOURCE_REQUESTS_QUERY } from '~/modules/resourcing/hooks/useResourceRequests';
import { RESOURCE_ALLOCATION_CHART_SUB_TAB_ENUM } from '../../hooks/useResourceAllocationChartTabStateContext';

export const getSourceMetadata = ({
  resourceRequest,
  isPtiEnabled = false,
  hiringStatus = HiringStatus.HiringWithdrawn
}) => {
  if (!isPtiEnabled) {
    return resourceRequest.sourceMetadata;
  }

  if (resourceRequest.requestStatus !== ResourceRequestStatus.Tobehired) {
    return resourceRequest.sourceMetadata;
  }

  return {
    ...resourceRequest.sourceMetadata,
    hiringStatus
  };
};

export const MARK_REQUEST_WITH_HIRING_SUBSTATUS = gql`
  mutation MarkToBeHiredResourceRequestWithHiringRequestedSubstatus(
    $input: MarkToBeHiredResourceRequestWithHiringRequestedSubStatusInput!
  ) {
    markToBeHiredResourceRequestWithHiringRequestedSubStatus(input: $input) {
      resourceRequest {
        ...SpecificResourceRequestWithAllocation
      }
    }
  }
  ${specificResourceRequestWithAllocationsFragment}
`;

const tryLoadCachedResourceRequestsQuery = ({
  proxy,
  optimistic,
  resourceRequestsQueryVariables
}) => {
  try {
    return proxy.readQuery(
      {
        query:
          resourceRequestsQueryVariables &&
          resourceRequestsQueryVariables.requestAccessLevel
            ? RESOURCING_RESOURCE_REQUESTS_QUERY
            : RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
        variables: resourceRequestsQueryVariables
      },
      optimistic
    );
  } catch (e) {
    return null;
  }
};

export const updateCacheForResourcingPlan = ({
  resourcePlanSubstatusFilter,
  resourceRequestsQueryVariables
}) => (proxy, mutationResponse) => {
  if (
    ![
      RESOURCE_ALLOCATION_CHART_SUB_TAB_ENUM.HIRINGREQUESTED,
      RESOURCE_ALLOCATION_CHART_SUB_TAB_ENUM.TOBEHIRED
    ].includes(resourcePlanSubstatusFilter)
  ) {
    return;
  }

  const {
    data: {
      isOptimistic,
      markToBeHiredResourceRequestWithHiringRequestedSubStatus: {
        resourceRequest: { id: resourceRequestId }
      }
    }
  } = mutationResponse;

  const cachedResourceRequests = tryLoadCachedResourceRequestsQuery({
    proxy,
    optimistic: isOptimistic,
    resourceRequestsQueryVariables
  });

  if (!cachedResourceRequests) return;

  const remainingResourceRequests = cachedResourceRequests.resourceRequests.resourceRequests.filter(
    request => request.id !== resourceRequestId
  );

  proxy.writeQuery({
    query:
      resourceRequestsQueryVariables &&
      resourceRequestsQueryVariables.requestAccessLevel
        ? RESOURCING_RESOURCE_REQUESTS_QUERY
        : RESOURCE_REQUESTS_ALLOCATIONS_QUERY,
    variables: resourceRequestsQueryVariables,
    data: {
      resourceRequests: {
        ...cachedResourceRequests.resourceRequests,
        resourceRequests: remainingResourceRequests
      }
    }
  });
};

const cacheUpdateFunctionsCalls = ({ cacheUpdateFunctions }) => (
  proxy,
  mutationResponse
) =>
  (cacheUpdateFunctions || []).forEach(func => func(proxy, mutationResponse));

export const useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus = ({
  cacheUpdateFunctions
} = {}) => {
  const [
    markToBeHiredResourceRequestWithHiringRequestedSubStatus
  ] = useMutation(MARK_REQUEST_WITH_HIRING_SUBSTATUS);

  const onMarkToBeHiredResourceRequestWithHiringRequestedSubStatus = useCallback(
    ({ resourceRequest, hiringStatus }) =>
      markToBeHiredResourceRequestWithHiringRequestedSubStatus({
        variables: {
          input: {
            resourceRequestId: resourceRequest.id,
            hiringStatus
          }
        },
        context: {
          debounceKey: `${resourceRequest.id}_hiring_status`,
          debounceTimeout: 100
        },
        optimisticResponse: {
          __typename: 'Mutation',
          markToBeHiredResourceRequestWithHiringRequestedSubStatus: {
            __typename:
              'MarkToBeHiredResourceRequestWithHiringRequestedSubStatusResult',
            isOptimistic: true,
            resourceRequest: {
              ...resourceRequest,
              sourceMetadata: {
                ...resourceRequest.sourceMetadata,
                hiringStatus,
                __typename: 'SourceMetadata'
              },
              __typename: 'ResourceRequest'
            }
          }
        },
        update: cacheUpdateFunctionsCalls({ cacheUpdateFunctions })
      }),
    [
      cacheUpdateFunctions,
      markToBeHiredResourceRequestWithHiringRequestedSubStatus
    ]
  );

  return {
    onMarkToBeHiredResourceRequestWithHiringRequestedSubStatus
  };
};

export default useMarkToBeHiredResourceRequestWithHiringRequestedSubStatus;
